<template>
  <div
    class="grid w-full grid-cols-7 items-center"
    :class="{ 'bg-gray-50': node.data.backup }"
  >
    <div
      v-if="node.data && getServiceProviderDetails(node.data.service)"
      v-tooltip="getServiceProviderDetails(node.data?.service)?.name || ''"
      class="w-fit-content grayscale hover:grayscale-0"
    >
      <img
        class="h-5 w-5"
        data-cy="service-image"
        :src="getProviderImageByServiceName(node.data?.service || '')"
        :alt="$t('ORCHESTRATION.SERVICE_IMAGE_ALT')"
        loading="lazy"
      />
    </div>
    <div v-else />
    <div class="col-span-2 flex items-center space-x-3">
      <div
        class="h-3 w-3 rounded-xs border-0"
        :style="{
          backgroundColor:
            node.data.color ??
            SCORECARD_COLOR_SWATCHES[
              node.data.index % SCORECARD_COLOR_SWATCHES.length
            ],
        }"
      />
      <p class="text-xs">{{ node.data.attribute }}</p>
      <div
        v-if="node.data.info"
        v-tooltip="{ content: node.data.info, position: 'right' }"
      >
        <icon-base :icon="IconInfo" />
      </div>
      <span
        v-if="node.data.backup_applied"
        class="badge-class text-xs whitespace-nowrap"
        data-cy="backup-applied"
      >
        {{ $t("SCORECARDS.BACKUP_APPLIED") }}
      </span>
      <div v-if="node.data.backup" class="badge-class" data-cy="backup">
        {{ $t("SCORECARDS.BACKUP") }}
      </div>
    </div>
    <span
      v-if="node.data.knockout"
      v-tooltip="
        getAttributeIsKnockedOut(node.data)
          ? { content: getKnockoutTooltip(node.data), placement: 'left' }
          : undefined
      "
    >
      <icon-base
        data-cy="icon-knockout"
        :icon="IconKnockout"
        :color="getKnockoutColor(node.data)"
      />
    </span>
    <span v-else />
    <span v-tooltip="getNodeValueTooltip(node.data)" data-cy="scorecard-value">
      {{
        showValue(node.data) ? truncateWithEllipsis(node.data.value, 40) : "-"
      }}
    </span>
    <span>
      {{ node.data.distribution }}
    </span>
    <span>
      {{ showValue(node.data) ? node.data.points : "-" }}
    </span>
  </div>
</template>

<script setup lang="ts">
import {
  SCORECARD_COLOR_SWATCHES,
  SERVICE_CUSTOM,
  UTIL_COLORS,
} from "@/helpers/constants";
import { truncateWithEllipsis } from "@/helpers/formatting";
import {
  formatKnockoutReasons,
  modelIsScorecardGroup,
} from "@/helpers/scorecards";
import { getProviderImageByServiceName } from "@/helpers/services";
import { useScorecardsServices } from "@/hooks/scorecards";

import IconInfo from "@/components/icons/IconInfo.vue";
import IconKnockout from "@/components/icons/IconKnockout.vue";

import type { IContextSetValueModel } from "@/models/orchestration";
import type {
  IScorecard,
  IScorecardContentCondition,
  IScorecardGroup,
  MappedTreeTableCategory,
  MappedTreeTableCategoryChild,
  MappedTreeTableCategoryChildData,
  MappedTreeTableCategoryData,
} from "@/models/scorecards";

interface Props {
  node: MappedTreeTableCategory | MappedTreeTableCategoryChild;
  pageIsView: boolean;
  model: IScorecard | IScorecardGroup;
  isReport: boolean;
}

const props = defineProps<Props>();

const { getKnockoutReasons, getServiceProviderDetails } =
  useScorecardsServices();

const getKnockoutTooltip = (data: {
  knockout_conditions?: IScorecardContentCondition[];
  attributeOriginal?: string;
}) => {
  if (props.pageIsView) {
    return formatKnockoutReasons("", data.knockout_conditions, false);
  }
  return getKnockoutReasons(props.model as IScorecard, data);
};

const getAttributeIsKnockedOut = (data: {
  attributeOriginal?: string;
  context?: IContextSetValueModel;
}) => {
  if (modelIsScorecardGroup(props.model) || !props.model?.results?.[0]) {
    return false;
  }
  return props.model.results[0].knockouts.some((ko) => {
    if (data.attributeOriginal === SERVICE_CUSTOM) {
      return ko.category.context?.value?.id === data.context?.value?.id;
    }
    return ko.category.attribute === data.attributeOriginal;
  });
};

const getKnockoutColor = (data: { attributeOriginal?: string }) => {
  return getAttributeIsKnockedOut(data) && !props.isReport
    ? UTIL_COLORS.WARNING
    : UTIL_COLORS.DEFAULT;
};

const getNodeValueTooltip = (
  data: MappedTreeTableCategoryData | MappedTreeTableCategoryChildData
) => ({
  content: data.value?.toString(),
  onShow: () => (data.value?.toString()?.length || 0) > 15 && showValue(data),
});

const showValue = (
  data: MappedTreeTableCategoryData | MappedTreeTableCategoryChildData
) => {
  return !data.backup_applied || data.backup;
};
</script>
